import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "photomoto_favs_v1";
const FavouritesContext = createContext(null);

function readInitial() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function FavouritesProvider({ children }) {
  const [ids, setIds] = useState(readInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch { /* quota */ }
  }, [ids]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue !== null) {
        try { setIds(JSON.parse(e.newValue) || []); } catch { /* ignore */ }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggleFav = useCallback((image) => {
    const id = typeof image === "string" ? image : image?.id;
    if (!id) return;
    setIds((prev) => {
      if (prev.includes(id)) {
        toast.info("Removed from favourites");
        return prev.filter((x) => x !== id);
      }
      toast.success("Saved to favourites ♥");
      return [...prev, id];
    });
  }, []);

  const removeFav = useCallback((id) => {
    setIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const pruneTo = useCallback((keepIds) => {
    setIds((prev) => prev.filter((x) => keepIds.includes(x)));
  }, []);

  const isFav = useCallback((id) => ids.includes(id), [ids]);

  const value = useMemo(
    () => ({ ids, count: ids.length, toggleFav, removeFav, pruneTo, isFav }),
    [ids, toggleFav, removeFav, pruneTo, isFav]
  );

  return <FavouritesContext.Provider value={value}>{children}</FavouritesContext.Provider>;
}

export function useFavourites() {
  const ctx = useContext(FavouritesContext);
  if (!ctx) throw new Error("useFavourites must be used within FavouritesProvider");
  return ctx;
}
